import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { Alert, Button, TextField } from '@mui/material';
import { isUrl } from '../../app/util';
import { classifyLink, normalizeUrlForComparison } from '../../util/sanitize-and-classify-links';
import { Link } from '../../app/feathers/shared/Link';
import { CandidateReferenceCategoryType } from '../../app/feathers/candidates/References';
import { MediaType } from '../../app/feathers/shared/MediaType';

interface AddSourceModalProps {
  type: CandidateReferenceCategoryType | 'other' | null;
  onAdd: (source: Link) => void;
  existingLinks: Link[];
  onCancel: () => void;
}

export const AddSourceModal: React.FC<AddSourceModalProps> = ({
  type,
  onAdd,
  existingLinks,
  onCancel
}) => {
  const [url, setUrl] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  let name: string;
  let titleEnabled: boolean;
  
  if(type === 'website') {
    name = 'candidate website';
    titleEnabled = false;
  } else if(type === 'social') {
    name = 'social media';
    titleEnabled = false;
  } else if(type === 'news') {
    titleEnabled = true;
    name = 'news article';
  } else if(type === 'other') {
    name = 'third-party source';
    titleEnabled = true;
  } else {
    name = 'source';
    titleEnabled = false;
  }

  const submittable = (
    url.length > 5 &&
    (titleEnabled ? title.length >= 3 : true)
  );

  const addSource = (): void => {
    if(!submittable || !type) return;
    setError(null);

    const existingUrls = existingLinks
      .map(s => s?.url)
      .filter((s): s is string => Boolean(s) && typeof s === 'string' && s.trim().length > 0)
      .map(normalizeUrlForComparison);

    const thisUrl = normalizeUrlForComparison(url);

    if(existingUrls.includes(thisUrl)) {
      setError(`The url '${thisUrl}' has already been added as a source.`);
      return;
    }

    let formattedSource: Link;
    if(type === 'website') {
      if(!isUrl(url)) {
        setError(`"${url}" is not a valid url.  Please check your inputs.`);
        return;
      }
      formattedSource = {
        mediaType: 'website' as MediaType,
        title: 'Candidate website',
        url: url
      };
    } else if(type === 'social') {
      const mediaType: MediaType = classifyLink(url, 'other') as MediaType;
      if(mediaType === 'other') {
        setError('This social media type is not supported. Please check your inputs.');
        return;
      } else {
        formattedSource = {
          mediaType: mediaType as MediaType,
          title: mediaType.slice(0, 1).toUpperCase() + mediaType.slice(1).toLowerCase(),
          url: url
        };
      }
    } else if(type === 'news') {
      if(!url || !title) {
        setError(`Please fill out all fields.`);
        return;
      }
      if(!isUrl(url)) {
        setError(`"${url}" is not a valid url.  Please check your inputs.`);
        return;
      }
      if(isUrl(title)) {
        setError(`"${title}" is not a valid title.  Please check your inputs.`);
        return;
      }
      formattedSource = {
        mediaType: 'other' as MediaType,
        title: title,
        url: url
      };
    } else if(type === 'other') {
      formattedSource = {
        mediaType: 'other' as MediaType,
        title: title || '',
        url: url
      };
    } else {
      return;
    }

    onAdd(formattedSource);
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridGap: '24px' }}>
      <Typography variant={'h2'}>New {name}</Typography>
      {
        error &&
        <Alert severity="error">{error}</Alert>
      }
      {
        titleEnabled &&
        <TextField
          onChange={(e) => setTitle(e.target.value)}
          value={title}
          placeholder="Article Title"
          size="small"
          fullWidth
        />
      }
      <TextField
        onChange={(e) => setUrl(e.target.value)}
        value={url}
        placeholder="URL (https://example.com)"
        size="small"
        fullWidth
      />
      <Button
        variant={'contained'}
        color={'primary'}
        disabled={!submittable}
        onClick={addSource}
      >
        Add source
      </Button>
    </div>
  );
};
